import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  Button,
  Badge,
  Input,
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Skeleton,
  ErrorState,
} from '@/components/ui'
import { Users, Search, MoreVertical, Shield, Mail, Trash2 } from 'lucide-react'
import { api, type DeveloperInfo } from '@/api/client'

const roleColors: Record<string, 'default' | 'success' | 'warning' | 'info'> = {
  owner: 'success',
  admin: 'warning',
  billing: 'info',
  developer: 'info',
  viewer: 'default',
}

export function TeamMembersPage() {
  const { orgSlug } = useParams()
  const [members, setMembers] = useState<DeveloperInfo[]>([])
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchMembers = async () => {
      if (!orgSlug) return

      try {
        setError(null)
        const response = await api.orgs.listMembers(orgSlug)
        setMembers(response.developers)
      } catch (err) {
        console.error('Failed to fetch members:', err)
        setError(err instanceof Error ? err.message : 'Failed to load team members')
      } finally {
        setIsLoading(false)
      }
    }

    fetchMembers()
  }, [orgSlug])

  const filteredMembers = members.filter(
    (m) =>
      (m.display_name?.toLowerCase() || '').includes(search.toLowerCase()) ||
      (m.email?.toLowerCase() || '').includes(search.toLowerCase())
  )

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="relative w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input
            placeholder="Search members..."
            className="pl-9"
            disabled
          />
        </div>
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border-subtle)]">
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">Member</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[var(--text-secondary)]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3].map((i) => (
                  <tr key={i} className="border-b border-[var(--border-subtle)] last:border-0">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div>
                          <Skeleton className="h-4 w-24 mb-1" />
                          <Skeleton className="h-3 w-32" />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4"><Skeleton className="h-5 w-16" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-20" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-16" /></td>
                    <td className="px-6 py-4 text-right"><Skeleton className="h-8 w-8 ml-auto" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="relative w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input
            placeholder="Search members..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <ErrorState
          title="Failed to load team members"
          description={error}
          action={
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative w-80">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]" />
        <Input
          placeholder="Search members..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Members list */}
      {filteredMembers.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No members found"
          description={search ? 'Try a different search term' : 'No team members yet'}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border-subtle)]">
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[var(--text-secondary)]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((member) => (
                  <tr
                    key={member.id}
                    className="border-b border-[var(--border-subtle)] last:border-0"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback>
                            {member.display_name?.split(' ').map((n) => n[0]).join('') ||
                             member.email?.[0]?.toUpperCase() || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium text-[var(--text-primary)]">
                            {member.display_name || 'No name'}
                          </p>
                          <p className="text-xs text-[var(--text-muted)]">
                            {member.email || 'No email'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={roleColors[member.role] || 'default'}>
                        {member.role}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-[var(--text-secondary)]">
                      {new Date(member.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-[var(--text-secondary)]">
                      <Badge variant="success">active</Badge>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Shield className="mr-2 h-4 w-4" />
                            Change role
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-[var(--error)]">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Remove member
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
